import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  CreditCard,
  Building,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Calendar,
  User,
  Phone,
  Mail,
  Search,
  Filter,
  RefreshCw,
  Download,
  Edit,
  Eye,
  Send,
  ArrowRight,
  FileText,
  Target,
  TrendingUp,
  Activity,
  Banknote,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import {
  getFinancialApplications,
  manageDisbursalProcess,
  generateDisbursalLetter,
} from "../../services/financialservice";

const DisbursalTracking = () => {
  const { userData: user } = useAuth();
  const [disbursals, setDisbursals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDisbursal, setSelectedDisbursal] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    lenderType: "",
    searchTerm: "",
    dateFrom: "",
    dateTo: "",
  });

  const [updateData, setUpdateData] = useState({
    status: "",
    disbursedAmount: "",
  disbursedDate: "",
  nachProcessed: false,
    transactionId: "",
    transactionDetails: {
      paymentMode: "",
      utr: "",
      bankName: "",
      payerAccount: "",
      payeeAccount: "",
      ifsc: "",
      upiId: "",
      referenceNote: "",
    },
    lenderDetails: {
      lenderName: "",
      accountNumber: "",
      ifscCode: "",
      disbursalMethod: "",
    },
    insuranceClaimDetails: {
      claimNumber: "",
      claimStatus: "",
      expectedRecoveryDate: "",
      recoveryAmount: "",
    },
    notes: "",
  });

  // Load disbursals
  const loadDisbursals = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both pre-disbursal and post-disbursal applications so they don't vanish after update
      const [preRes, postRes] = await Promise.all([
        getFinancialApplications({ ...filters, status: "settlement_approved" }, 1, 50),
        getFinancialApplications({ ...filters, status: "amount_disbursed" }, 1, 50)
      ]);

      const apps = [];
      if (preRes?.success && Array.isArray(preRes.applications)) apps.push(...preRes.applications);
      if (postRes?.success && Array.isArray(postRes.applications)) apps.push(...postRes.applications);

      // De-duplicate by applicationId
      const mapByAppId = new Map();
      for (const a of apps) {
        mapByAppId.set(a.applicationId || a.id, a);
      }

      // Keep ones relevant to disbursal tracking
      let disbursalApplications = Array.from(mapByAppId.values()).filter(
        (app) => app.workflow?.dischargeSettlement?.status === "approved" || app.workflow?.disbursal?.status
      );

      // Apply UI status filter if provided (on disbursal status)
      if (filters.status) {
        disbursalApplications = disbursalApplications.filter(
          (app) => (app.workflow?.disbursal?.status || "").toLowerCase() === filters.status.toLowerCase()
        );
      }

      // Sort: non-disbursed first, disbursed at the bottom for ongoing visibility
      disbursalApplications.sort((a, b) => {
        const aDisbursed = (a.workflow?.disbursal?.status === "disbursed") ? 1 : 0;
        const bDisbursed = (b.workflow?.disbursal?.status === "disbursed") ? 1 : 0;
        if (aDisbursed !== bDisbursed) return aDisbursed - bDisbursed; // 0 before 1
        // Secondary: recent first by submittedAt or updated disbursal date
        const aTime = (a.workflow?.disbursal?.disbursedDate || a.submittedAt) ? new Date(a.workflow?.disbursal?.disbursedDate || a.submittedAt).getTime() : 0;
        const bTime = (b.workflow?.disbursal?.disbursedDate || b.submittedAt) ? new Date(b.workflow?.disbursal?.disbursedDate || b.submittedAt).getTime() : 0;
        return bTime - aTime;
      });

      setDisbursals(disbursalApplications);
    } catch (error) {
      console.error("Error loading disbursals:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDisbursals();
  }, [loadDisbursals]);

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatForDateTimeLocal = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      ready_for_disbursal: {
        color: "bg-blue-100 text-blue-800",
        text: "Ready for Disbursal",
      },
      in_progress: {
        color: "bg-orange-100 text-orange-800",
        text: "In Progress",
      },
      disbursed: { color: "bg-green-100 text-green-800", text: "Disbursed" },
      failed: { color: "bg-red-100 text-red-800", text: "Failed" },
      on_hold: { color: "bg-gray-100 text-gray-800", text: "On Hold" },
    };

    const config = statusConfig[status] || {
      color: "bg-gray-100 text-gray-800",
      text: status,
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.text}
      </span>
    );
  };

  const openUpdateModal = (disbursal) => {
    setSelectedDisbursal(disbursal);

    // Pre-populate with existing data
    const disbursalData = disbursal.workflow?.disbursal || {};
    const settlementData = disbursal.workflow?.dischargeSettlement || {};

    setUpdateData({
      status: disbursalData.status || "ready_for_disbursal",
      disbursedAmount:
        disbursalData.disbursedAmount ||
        settlementData.finalSettlementAmount ||
        "",
  disbursedDate: formatForDateTimeLocal(disbursalData.disbursedDate) || "",
  nachProcessed: Boolean(disbursalData.nachProcessed) || false,
      transactionId: disbursalData.transactionId || "",
      transactionDetails: {
        paymentMode:
          disbursalData.transactionDetails?.paymentMode ||
          disbursalData.lenderDetails?.disbursalMethod ||
          "neft",
        utr: disbursalData.transactionDetails?.utr || "",
        bankName: disbursalData.transactionDetails?.bankName || "",
        payerAccount: disbursalData.transactionDetails?.payerAccount || "",
        payeeAccount: disbursalData.transactionDetails?.payeeAccount || "",
        ifsc: disbursalData.transactionDetails?.ifsc || "",
        upiId: disbursalData.transactionDetails?.upiId || "",
        referenceNote: disbursalData.transactionDetails?.referenceNote || "",
      },
      lenderDetails: {
        lenderName: disbursalData.lenderDetails?.lenderName || "",
        accountNumber: disbursalData.lenderDetails?.accountNumber || "",
        ifscCode: disbursalData.lenderDetails?.ifscCode || "",
        disbursalMethod: disbursalData.lenderDetails?.disbursalMethod || "neft",
      },
      insuranceClaimDetails: {
        claimNumber: disbursalData.insuranceClaimDetails?.claimNumber || "",
        claimStatus:
          disbursalData.insuranceClaimDetails?.claimStatus || "initiated",
        expectedRecoveryDate:
          disbursalData.insuranceClaimDetails?.expectedRecoveryDate || "",
        recoveryAmount:
          disbursalData.insuranceClaimDetails?.recoveryAmount || "",
      },
      notes: disbursalData.notes || "",
    });

    setShowUpdateModal(true);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedDisbursal || !user?.uid) return;

    try {
      const result = await manageDisbursalProcess(
        selectedDisbursal.applicationId,
        user.uid,
        updateData
      );

      if (result.success) {
        setShowUpdateModal(false);
        setSelectedDisbursal(null);
        loadDisbursals();
        alert("Disbursal updated successfully!");
      } else {
        alert("Error updating disbursal: " + result.error);
      }
    } catch (error) {
      console.error("Error updating disbursal:", error);
      alert("Error updating disbursal");
    }
  };

  const handleGenerateLetter = async (applicationId) => {
    try {
      const result = await generateDisbursalLetter(applicationId, user.uid);
      if (result.success) {
        alert("Disbursal letter generated successfully!");
        loadDisbursals();
      } else {
        alert("Error generating letter: " + result.error);
      }
    } catch (error) {
      console.error("Error generating letter:", error);
      alert("Error generating letter");
    }
  };

  // Stable update callbacks to prevent modal remounts and avoid input focus loss
  const handleUpdateDataChange = useCallback((updater) => {
    if (typeof updater === 'function') {
      setUpdateData(updater);
    } else {
      setUpdateData((prev) => ({ ...prev, ...updater }));
    }
  }, []);

  const handleModalClose = useCallback(() => {
    setShowUpdateModal(false);
    setSelectedDisbursal(null);
  }, []);

  const DisbursalCard = ({ disbursal }) => {
    const disbursalData = disbursal.workflow?.disbursal || {};
    const settlementData = disbursal.workflow?.dischargeSettlement || {};
    const finalAmount = settlementData.finalSettlementAmount || 0;
    const isDisbursed = disbursalData.status === "disbursed";

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {disbursal.applicationId}
              </h3>
              {getStatusBadge(disbursalData.status || "ready_for_disbursal")}
              {disbursal.emergency && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Emergency
                </span>
              )}
            </div>
            {/* Show compact view for disbursed items to keep them visible but unobtrusive */}
            {!isDisbursed ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Patient & Hospital Info */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <User className="h-4 w-4 mr-2 text-teal-600" />
                  Patient & Hospital
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>
                    <strong>Patient:</strong> {disbursal.patient?.name}
                  </div>
                  <div>
                    <strong>Hospital:</strong> {disbursal.hospitalName}
                  </div>
                  <div>
                    <strong>Diagnosis:</strong> {disbursal.medical?.diagnosis}
                  </div>
                  <div>
                    <strong>Policy:</strong>{" "}
                    {disbursal.policy?.insuranceCompany}
                  </div>
                </div>
              </div>

              {/* Financial Details */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-teal-600" />
                  Financial Details
                </h4>
                <div className="text-sm space-y-1">
                  <div className="text-blue-600">
                    <strong>Settlement Amount:</strong>{" "}
                    {formatCurrency(finalAmount)}
                  </div>
                  {disbursalData.disbursedAmount && (
                    <div className="text-green-600">
                      <strong>Disbursed:</strong>{" "}
                      {formatCurrency(disbursalData.disbursedAmount)}
                    </div>
                  )}
                  {typeof disbursalData.nachProcessed === "boolean" && (
                    <div className="text-gray-600">
                      <strong>NACH:</strong>{" "}
                      {disbursalData.nachProcessed ? "Yes" : "No"}
                    </div>
                  )}
                  {disbursalData.transactionId && (
                    <div className="text-gray-600">
                      <strong>Transaction ID:</strong>{" "}
                      {disbursalData.transactionId}
                    </div>
                  )}
                  {disbursalData.transactionDetails?.utr && (
                    <div className="text-gray-600">
                      <strong>UTR:</strong> {disbursalData.transactionDetails.utr}
                    </div>
                  )}
                  {disbursalData.transactionDetails?.paymentMode && (
                    <div className="text-gray-600">
                      <strong>Mode:</strong> {disbursalData.transactionDetails.paymentMode?.toUpperCase?.() || disbursalData.transactionDetails.paymentMode}
                    </div>
                  )}
                  {disbursalData.disbursedDate && (
                    <div className="text-gray-600">
                      <strong>Disbursed On:</strong>{" "}
                      {formatDate(disbursalData.disbursedDate)}
                    </div>
                  )}
                </div>
              </div>

              {/* Lender & Recovery Info */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Building className="h-4 w-4 mr-2 text-teal-600" />
                  Lender & Recovery
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  {disbursalData.lenderDetails?.lenderName && (
                    <div>
                      <strong>Lender:</strong>{" "}
                      {disbursalData.lenderDetails.lenderName}
                    </div>
                  )}
                  {disbursalData.insuranceClaimDetails?.claimNumber && (
                    <div>
                      <strong>Claim No:</strong>{" "}
                      {disbursalData.insuranceClaimDetails.claimNumber}
                    </div>
                  )}
                  {disbursalData.insuranceClaimDetails?.claimStatus && (
                    <div>
                      <strong>Claim Status:</strong>
                      <span
                        className={`ml-1 ${
                          disbursalData.insuranceClaimDetails.claimStatus ===
                          "approved"
                            ? "text-green-600"
                            : disbursalData.insuranceClaimDetails
                                .claimStatus === "pending"
                            ? "text-yellow-600"
                            : "text-gray-600"
                        }`}
                      >
                        {disbursalData.insuranceClaimDetails.claimStatus}
                      </span>
                    </div>
                  )}
                  <div>
                    <strong>Submitted:</strong>{" "}
                    {formatDate(disbursal.submittedAt)}
                  </div>
                </div>
              </div>
            </div>
            ) : (
              <div className="text-sm text-gray-700">
                {/* Compact summary when already disbursed */}
                <div className="flex flex-wrap gap-4">
                  <span><strong>Disbursed:</strong> {disbursalData.disbursedAmount ? formatCurrency(disbursalData.disbursedAmount) : '-'}</span>
                  <span><strong>Date:</strong> {formatDate(disbursalData.disbursedDate)}</span>
                  {disbursalData.transactionId && (
                    <span><strong>Txn:</strong> {disbursalData.transactionId}</span>
                  )}
                  {disbursalData.transactionDetails?.utr && (
                    <span><strong>UTR:</strong> {disbursalData.transactionDetails.utr}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-2 ml-4">
            <button
              onClick={() => openUpdateModal(disbursal)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
            >
              <Edit className="h-4 w-4" />
              <span>Update Status</span>
            </button>

            {!disbursalData.letterGenerated && (
              <button
                onClick={() => handleGenerateLetter(disbursal.applicationId)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <FileText className="h-4 w-4" />
                <span>Generate Letter</span>
              </button>
            )}

            <button
              onClick={() => setSelectedDisbursal(disbursal)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              <Eye className="h-4 w-4" />
              <span>View Details</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const UpdateDisbursalModal = React.memo(function UpdateDisbursalModal({
    selectedDisbursal,
    updateData,
    setUpdateData,
    onClose,
    onSubmit
  }) {
    const firstFieldRef = useRef(null);
    useEffect(() => {
      // Autofocus the first field when modal opens
      firstFieldRef.current?.focus();
    }, []);

    // Stable handlers to avoid regenerating functions during typing
    const handleStatusChange = useCallback((value) => {
      setUpdateData((prev) => ({ ...prev, status: value }));
    }, [setUpdateData]);

    const handleDisbursedAmountChange = useCallback((value) => {
      setUpdateData((prev) => ({ ...prev, disbursedAmount: value }));
    }, [setUpdateData]);

    const handleDisbursedDateChange = useCallback((value) => {
      setUpdateData((prev) => ({ ...prev, disbursedDate: value }));
    }, [setUpdateData]);

    const handleTransactionIdChange = useCallback((value) => {
      setUpdateData((prev) => ({ ...prev, transactionId: value }));
    }, [setUpdateData]);

    const handleNachProcessedChange = useCallback((value) => {
      setUpdateData((prev) => ({ ...prev, nachProcessed: value }));
    }, [setUpdateData]);

    // Transaction details
    const handleTxnDetailsChange = useCallback((field, value) => {
      setUpdateData((prev) => ({
        ...prev,
        transactionDetails: {
          ...prev.transactionDetails,
          [field]: value,
        },
      }));
    }, [setUpdateData]);

    // Lender details
    const handleLenderNameChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        lenderDetails: { ...prev.lenderDetails, lenderName: value },
      }));
    }, [setUpdateData]);

    const handleAccountNumberChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        lenderDetails: { ...prev.lenderDetails, accountNumber: value },
      }));
    }, [setUpdateData]);

    const handleIfscChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        lenderDetails: { ...prev.lenderDetails, ifscCode: value },
      }));
    }, [setUpdateData]);

    const handleDisbursalMethodChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        lenderDetails: { ...prev.lenderDetails, disbursalMethod: value },
      }));
    }, [setUpdateData]);

    // Insurance claim details
    const handleClaimNumberChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        insuranceClaimDetails: { ...prev.insuranceClaimDetails, claimNumber: value },
      }));
    }, [setUpdateData]);

    const handleClaimStatusChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        insuranceClaimDetails: { ...prev.insuranceClaimDetails, claimStatus: value },
      }));
    }, [setUpdateData]);

    const handleExpectedRecoveryDateChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        insuranceClaimDetails: { ...prev.insuranceClaimDetails, expectedRecoveryDate: value },
      }));
    }, [setUpdateData]);

    const handleRecoveryAmountChange = useCallback((value) => {
      setUpdateData((prev) => ({
        ...prev,
        insuranceClaimDetails: { ...prev.insuranceClaimDetails, recoveryAmount: value },
      }));
    }, [setUpdateData]);

    const handleNotesChange = useCallback((value) => {
      setUpdateData((prev) => ({ ...prev, notes: value }));
    }, [setUpdateData]);

    const modalContent = (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto overscroll-contain" onWheel={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Update Disbursal Status</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Application ID: {selectedDisbursal.applicationId} | Patient: {selectedDisbursal.patient?.name}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Disbursal Status */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Disbursal Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={updateData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="ready_for_disbursal">
                      Ready for Disbursal
                    </option>
                    <option value="in_progress">In Progress</option>
                    <option value="disbursed">Disbursed</option>
                    <option value="failed">Failed</option>
                    <option value="on_hold">On Hold</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount Disbursed
                  </label>
                  <input
                    type="number"
                    value={updateData.disbursedAmount}
                    onChange={(e) => handleDisbursedAmountChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter disbursed amount"
                    ref={firstFieldRef}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Disbursal Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={updateData.disbursedDate}
                    onChange={(e) => handleDisbursedDateChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionId}
                    onChange={(e) => handleTransactionIdChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter transaction ID"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NACH Processed
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="nachProcessed"
                        className="text-teal-600"
                        checked={updateData.nachProcessed === true}
                        onChange={() => handleNachProcessedChange(true)}
                      />
                      <span className="ml-2 text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="nachProcessed"
                        className="text-teal-600"
                        checked={updateData.nachProcessed === false}
                        onChange={() => handleNachProcessedChange(false)}
                      />
                      <span className="ml-2 text-sm text-gray-700">No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="bg-amber-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Transaction Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Mode
                  </label>
                  <select
                    value={updateData.transactionDetails.paymentMode}
                    onChange={(e) => handleTxnDetailsChange("paymentMode", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="neft">NEFT</option>
                    <option value="rtgs">RTGS</option>
                    <option value="imps">IMPS</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="dd">Demand Draft</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    UTR / Reference No.
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.utr}
                    onChange={(e) => handleTxnDetailsChange("utr", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter UTR or reference number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.bankName}
                    onChange={(e) => handleTxnDetailsChange("bankName", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Account
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.payerAccount}
                    onChange={(e) => handleTxnDetailsChange("payerAccount", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Payer account number (last 4 ok)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Account
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.payeeAccount}
                    onChange={(e) => handleTxnDetailsChange("payeeAccount", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Beneficiary account number (last 4 ok)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.ifsc}
                    onChange={(e) => handleTxnDetailsChange("ifsc", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter IFSC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    UPI ID (if applicable)
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.upiId}
                    onChange={(e) => handleTxnDetailsChange("upiId", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="name@bank"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Note
                  </label>
                  <input
                    type="text"
                    value={updateData.transactionDetails.referenceNote}
                    onChange={(e) => handleTxnDetailsChange("referenceNote", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Any memo/remarks for this transaction"
                  />
                </div>
              </div>
            </div>

            {/* Lender Details */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Lender Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lender Name
                  </label>
                  <input
                    type="text"
                    value={updateData.lenderDetails.lenderName}
                    onChange={(e) => handleLenderNameChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter lender name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={updateData.lenderDetails.accountNumber}
                    onChange={(e) => handleAccountNumberChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter account number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    value={updateData.lenderDetails.ifscCode}
                    onChange={(e) => handleIfscChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter IFSC code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Disbursal Method
                  </label>
                  <select
                    value={updateData.lenderDetails.disbursalMethod}
                    onChange={(e) => handleDisbursalMethodChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="neft">NEFT</option>
                    <option value="rtgs">RTGS</option>
                    <option value="imps">IMPS</option>
                    <option value="cheque">Cheque</option>
                    <option value="dd">Demand Draft</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Insurance Claim Details */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Insurance Claim Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Number
                  </label>
                  <input
                    type="text"
                    value={updateData.insuranceClaimDetails.claimNumber}
                    onChange={(e) => handleClaimNumberChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter insurance claim number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Claim Status
                  </label>
                  <select
                    value={updateData.insuranceClaimDetails.claimStatus}
                    onChange={(e) => handleClaimStatusChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="initiated">Initiated</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expected Recovery Date
                  </label>
                  <input
                    type="date"
                    value={
                      updateData.insuranceClaimDetails.expectedRecoveryDate
                    }
                    onChange={(e) => handleExpectedRecoveryDateChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recovery Amount
                  </label>
                  <input
                    type="number"
                    value={updateData.insuranceClaimDetails.recoveryAmount}
                    onChange={(e) => handleRecoveryAmountChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                    placeholder="Expected recovery amount"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={updateData.notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500"
                placeholder="Add any notes about the disbursal..."
              />
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Update Disbursal
            </button>
          </div>
        </div>
      </div>
  );

    return createPortal(modalContent, document.body);
  });

  // Improve modal UX: lock background scroll when modal open to prevent page jumping
  useEffect(() => {
    if (showUpdateModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showUpdateModal]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Disbursal Tracking
          </h1>
          <p className="text-gray-600">
            Track and manage disbursements to lenders
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-600">
            {disbursals.length} disbursals
          </span>
          <button
            onClick={loadDisbursals}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search applications..."
              value={filters.searchTerm}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, searchTerm: e.target.value }))
              }
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, status: e.target.value }))
            }
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Status</option>
            <option value="ready_for_disbursal">Ready for Disbursal</option>
            <option value="in_progress">In Progress</option>
            <option value="disbursed">Disbursed</option>
            <option value="failed">Failed</option>
          </select>

          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
            }
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
            placeholder="From Date"
          />

          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
            }
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
            placeholder="To Date"
          />
        </div>
      </div>

      {/* Disbursals List */}
      {disbursals.length > 0 ? (
        <div className="space-y-4">
          {disbursals.map((disbursal) => (
            <DisbursalCard key={disbursal.id} disbursal={disbursal} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Disbursals Found
          </h3>
          <p className="text-gray-600">
            No applications are ready for disbursal at the moment.
          </p>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedDisbursal && (
        <UpdateDisbursalModal
          key={selectedDisbursal.applicationId}
          selectedDisbursal={selectedDisbursal}
          updateData={updateData}
          setUpdateData={handleUpdateDataChange}
          onClose={handleModalClose}
          onSubmit={handleUpdateSubmit}
        />
      )}
    </div>
  );
};

export default DisbursalTracking;
